import React, { useState, useCallback } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useAppSelector } from "src/app/hooks";
import { selectAuth } from "src/features/authSlice";
import { callApi, HttpMethod } from "src/services/apiService";
import AccountUsagePanel from "src/components/AccountUsagePanel";
import AccountSalesSummary from "src/components/AccountSalesSummary";

interface InvoiceLink {
  InvoiceId: number;
  InvoiceDate: string;
  Total: number;
}

interface AccountSearchResult {
  AcctId: string;
  Customer: string;
  BasePlan: string;
  LastInvoice: string;
  SalesRep: string;
  DncScrub: string;
  LitigatorScrub: string;
  TrustCall: string;
  Remediation: string;
  CallBranding: string;
  ReassignedNumber: string;
  ComplianceGuide: string;
  DataServices: string;
  Portability: string;
  Invoices?: InvoiceLink[];
}

type BillableCountState = "loading" | "error" | number;

const LeadLookup: React.FC = () => {
  const { hasSalesReportAccess, isAuthenticated } = useAppSelector(selectAuth);
  // Sysadmin debug hack: ?asUser=Some Rep forwards to the API so a sysadmin
  // can preview what that rep would see (server-side gate enforces the
  // sysadmin-only check; non-sysadmins can't impersonate even with the param).
  const [searchParams] = useSearchParams();
  const asUser = searchParams.get("asUser") ?? "";
  const asUserSuffix = asUser ? `&asUser=${encodeURIComponent(asUser)}` : "";
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<AccountSearchResult[]>([]);
  const [billableCounts, setBillableCounts] = useState<Record<string, BillableCountState>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  // Set of AcctIds whose details panel is expanded. Multiple rows can be
  // expanded simultaneously so reps can compare accounts side-by-side.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpanded = (acctId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(acctId)) next.delete(acctId);
      else next.add(acctId);
      return next;
    });
  };

  if (isAuthenticated && hasSalesReportAccess === false) {
    return <Navigate to="/home" replace />;
  }

  const handleSearch = useCallback(async () => {
    const trimmed = searchTerm.trim();
    if (!trimmed) return;

    setIsLoading(true);
    setError("");
    setHasSearched(true);

    try {
      const response: AccountSearchResult[] = await callApi(
        `sales/lead-lookup/Search?q=${encodeURIComponent(trimmed)}${asUserSuffix}`,
        HttpMethod.GET,
      );
      const rows = response ?? [];
      setResults(rows);

      // Billable count is a slow Sessions-table aggregation, so it runs out of
      // band: one parallel request per owned account after the table renders.
      // Owned == backend returned invoices for the row (same gate as the
      // existing product columns).
      const owned = rows.filter((r) => (r.Invoices?.length ?? 0) > 0);
      setBillableCounts(
        Object.fromEntries(owned.map((r) => [r.AcctId, "loading" as BillableCountState])),
      );
      owned.forEach(async (r) => {
        try {
          const res = await callApi(
            `sales/lead-lookup/BillableCount?customerAcctId=${encodeURIComponent(r.AcctId)}${asUserSuffix}`,
            HttpMethod.GET,
          );
          setBillableCounts((prev) => ({ ...prev, [r.AcctId]: res.BillableCount as number }));
        } catch (e) {
          console.error("BillableCount failed for", r.AcctId, e);
          setBillableCounts((prev) => ({ ...prev, [r.AcctId]: "error" }));
        }
      });
    } catch (err: any) {
      console.error("Error looking up leads:", err);
      setError(err.message || "An error occurred while searching.");
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, asUserSuffix]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const columns = [
    { key: "Customer", label: "Customer", isProduct: false },
    { key: "BasePlan", label: "Base Plan", isProduct: false },
    { key: "LastInvoice", label: "Last Invoice", isProduct: false },
    { key: "SalesRep", label: "Sales Rep", isProduct: false },
    { key: "DncScrub", label: "DNC Scrub", isProduct: true },
    { key: "LitigatorScrub", label: "Litigator Scrub", isProduct: true },
    { key: "TrustCall", label: "TrustCall", isProduct: true },
    { key: "Remediation", label: "Remediation", isProduct: true },
    { key: "CallBranding", label: "Call Branding", isProduct: true },
    { key: "ReassignedNumber", label: "Reassigned Number", isProduct: true },
    { key: "ComplianceGuide", label: "Compliance Guide", isProduct: true },
    { key: "DataServices", label: "Data Services", isProduct: true },
    { key: "Portability", label: "Portability", isProduct: true },
    { key: "BillableCount", label: "Current Billable DNC", isProduct: true },
    { key: "Invoices", label: "Recent Invoices", isProduct: true },
  ];

  return (
    <div className="container-fluid p-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h4 className="m-0">
          Lead Lookup
          {asUser && (
            <span className="badge bg-warning text-dark ms-2" style={{ fontSize: "0.7rem" }}>
              Viewing as: {asUser}
            </span>
          )}
        </h4>
        <Link to="/t/sales-production-report" className="btn btn-sm btn-outline-secondary">
          <i className="bi bi-arrow-left"></i> Sales Report
        </Link>
      </div>
      <p className="text-muted mb-3" style={{ fontSize: "0.85rem" }}>
        Check whether a prospect is already an account before pitching them.
      </p>

      <div className="d-flex align-items-center gap-2 mb-3">
        <input
          type="text"
          className="form-control"
          style={{ maxWidth: 400 }}
          placeholder="Search by account name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
        />
        <button
          className="btn btn-primary"
          onClick={handleSearch}
          disabled={isLoading || !searchTerm.trim()}
        >
          {isLoading ? "Searching..." : "Search"}
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {hasSearched && !isLoading && results.length === 0 && !error && (
        <div className="alert alert-info">No results found.</div>
      )}

      {results.length > 0 && (
        <div
          className="protected-content"
          style={{ userSelect: "none", WebkitUserSelect: "none" }}
          onCopy={(e) => e.preventDefault()}
          onCut={(e) => e.preventDefault()}
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
        >
          <div className="table-responsive lead-lookup-table-scroll">
            <table className="table table-striped table-bordered table-sm">
              <thead className="table-dark">
                <tr>
                  {/* Expand-row toggle column. No header label needed — the
                      chevrons in each cell make the affordance obvious. */}
                  <th style={{ width: "1%" }}></th>
                  {columns.map((col) => (
                    <th key={col.key} className="text-nowrap">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results.map((row, idx) => {
                  const isOwnedRow = (row.Invoices?.length ?? 0) > 0;
                  const isExpanded = expanded.has(row.AcctId);
                  return (
                  <React.Fragment key={idx}>
                  <tr>
                    <td className="text-center align-middle">
                      {/* Only owned rows can be expanded — the AccountUsage
                          endpoints on the backend gate by the same ownership
                          check the row mask uses. */}
                      {isOwnedRow ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0"
                          aria-label={isExpanded ? "Collapse details" : "Expand details"}
                          onClick={() => toggleExpanded(row.AcctId)}
                        >
                          <i className={`bi ${isExpanded ? "bi-chevron-down" : "bi-chevron-right"}`} />
                        </button>
                      ) : null}
                    </td>
                    {columns.map((col) => {
                      const value = row[col.key as keyof AccountSearchResult];
                      const isInvoices = col.key === "Invoices";
                      const isBillable = col.key === "BillableCount";
                      const invoices = isInvoices ? row.Invoices : undefined;
                      const billable = isBillable ? billableCounts[row.AcctId] : undefined;
                      const isOwned = isOwnedRow;
                      const isLocked =
                        col.isProduct &&
                        (isInvoices
                          ? !invoices || invoices.length === 0
                          : isBillable
                          ? !isOwned
                          : !value);
                      return (
                        <td
                          key={col.key}
                          className={col.isProduct ? "text-center" : ""}
                        >
                          {isLocked ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#999"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <rect
                                x="3"
                                y="11"
                                width="18"
                                height="11"
                                rx="2"
                                ry="2"
                              />
                              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                          ) : isInvoices ? (
                            <div className="d-flex flex-column align-items-center">
                              {invoices!.map((inv) => (
                                <a
                                  key={inv.InvoiceId}
                                  href={`/app/admin/b-invoicePDF/${inv.InvoiceId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-nowrap"
                                >
                                  {inv.InvoiceDate}
                                </a>
                              ))}
                            </div>
                          ) : isBillable ? (
                            billable === "loading" ? (
                              <span
                                className="spinner-border spinner-border-sm text-secondary"
                                role="status"
                                aria-label="Loading billable count"
                              />
                            ) : billable === "error" ? (
                              <span className="text-muted">—</span>
                            ) : typeof billable === "number" ? (
                              billable.toLocaleString()
                            ) : (
                              <span className="text-muted">—</span>
                            )
                          ) : (
                            value as React.ReactNode
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={columns.length + 1} className="bg-light">
                        <div className="p-2">
                          <AccountSalesSummary
                            customerAcctId={row.AcctId}
                            asUserSuffix={asUserSuffix}
                          />
                          <AccountUsagePanel
                            endpointBase="sales/lead-lookup"
                            customerAcctId={row.AcctId}
                            asUserSuffix={asUserSuffix}
                            audience="sales-rep"
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div
            className="d-flex justify-content-between mt-1"
            style={{ fontSize: "0.8rem" }}
          >
            <span className="text-muted">
              Showing up to {results.length} result(s).
            </span>
            <span className="text-muted fst-italic">
              * Product information is only shown when you are the sales rep of
              the account.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadLookup;
